import React from 'react'
import { isEmpty } from 'lodash'
import i18n from 'config/i18n'
import { TASK_EVENT_TYPE, TASK_PROGRESS } from 'types/enums'

import {
  progressLogContent,
  IResponseProgressLogs,
  sourceImage,
} from 'features/Ticket'
import {
  setValue,
  setFormatTime,
  setFormatAssignDate,
  getPhoneFormatter,
} from 'helpers/common'
import { getImageFromReporter } from 'helpers/microsite'
import { getPhotoContent, getProductPhotos } from './getPhotoContent'
import { getReasonContent } from './getReasonContent'

import type {
  IUploadPhoto,
  ITaskTransactionProduct,
  IUpdateDetailProduct,
} from 'features/Ticket'

export const setProgressLogContent = (
  typeEvent: string,
  index: number,
  progressLogs: IResponseProgressLogs[],
  renderStatus: (status: string) => '-' | JSX.Element,
  renderPreviewImages: (list: sourceImage, text: string) => JSX.Element,
  getProductValue: (
    updateDetail: IUpdateDetailProduct | null,
    list: ITaskTransactionProduct[],
  ) => ITaskTransactionProduct | null,
): progressLogContent => {
  const {
    assignStart,
    assignEnd,
    code,
    checkInAt,
    contactName,
    contactPhoneNumber,
    contactEmail,
    branchName,
    companyName,
    taskIssue,
    type: taskType,
    issueDetail,
    updatedByFullName,
    checkInByUser,
    users,
    pendingCount,
    pendingByUser,
    pendingReasons,
    pendingDetail,
    pendingAt,
    completedByUser,
    completedTaskIssue,
    completedDetail,
    rejectedReasons,
    rejectedDetail,
    closedTaskReasons,
    closedTaskDetail,
    beforePhotos: assignBeforePhotos,
    afterPhotos: assignAfterPhotos,
    fileUploads,
    transferringAt,
    updateDetail,
    taskTransactionProducts,
  } = progressLogs[index]

  const {
    taskIssueValue,
    taskTypeValue,
    pendingReasonValue,
    completedTaskIssueValue,
    rejectedReasonValue,
    closedReasonValue,
  } = getReasonContent(
    taskIssue,
    taskType,
    pendingReasons,
    completedTaskIssue,
    rejectedReasons,
    closedTaskReasons,
  )

  const product = getProductValue(updateDetail, taskTransactionProducts)
  const hasDetailAndTransaction =
    updateDetail && taskTransactionProducts && product
  const productName = hasDetailAndTransaction ? product.productName : '-'

  let content: progressLogContent = {
    status: '-',
    subject: '-',
    updatedByFullName: '-',
    detail: '-',
  }

  const getName = (name: string | null, alternateName: string | null) => {
    let value = '-'

    if (name) {
      value = setValue(name)
    } else if (alternateName) {
      value = setValue(alternateName)
    }

    return value
  }
  switch (typeEvent) {
    case TASK_EVENT_TYPE.TASK_CREATED_BY_ADMIN:
      const _isShowImage = fileUploads && fileUploads.length > 0
      const _imageList = _isShowImage ? getImageFromReporter(fileUploads) : []
      const _detailCreatedCustomerText = i18n.t('ticket:detail.createdAdmin', {
        adminFullName: setValue(updatedByFullName),
        taskIssue: setValue(taskIssueValue),
        detail: setValue(issueDetail),
        interpolation: { escapeValue: false },
      })
      content = {
        status: renderStatus(TASK_PROGRESS.NEW),
        subject: i18n.t('ticket:subject.createdAdmin'),
        detail: _isShowImage
          ? renderPreviewImages(_imageList, _detailCreatedCustomerText)
          : _detailCreatedCustomerText,
      }
      break

    case TASK_EVENT_TYPE.TASK_CREATED_BY_CUSTOMER:
      const isShowImage = fileUploads && fileUploads.length > 0
      const imageList = isShowImage ? getImageFromReporter(fileUploads) : []
      const detailCreatedCustomerText = i18n.t(
        'ticket:detail.createdCustomer',
        {
          name: setValue(contactName),
          phoneNumber: contactPhoneNumber
            ? getPhoneFormatter(contactPhoneNumber)
            : '-',
          email: setValue(contactEmail),
          branchName: setValue(branchName),
          companyName: setValue(companyName),
          taskIssue: setValue(taskIssueValue),
          taskType: setValue(taskTypeValue),
          taskDetail: setValue(issueDetail),
          interpolation: { escapeValue: false },
        },
      )

      content = {
        status: renderStatus(TASK_PROGRESS.NEW),
        subject: i18n.t('ticket:subject.createdCustomer'),
        detail: isShowImage
          ? renderPreviewImages(imageList, detailCreatedCustomerText)
          : detailCreatedCustomerText,
      }
      break

    case TASK_EVENT_TYPE.TASK_CREATED_BY_SYSTEM:
      content = {
        status: renderStatus(TASK_PROGRESS.NEW),
        subject: i18n.t('ticket:subject.createdSystem'),
        detail: i18n.t('ticket:detail.createdSystem'),
      }
      break

    case TASK_EVENT_TYPE.TASK_ASSIGNED:
      content = {
        status: renderStatus(TASK_PROGRESS.IN_PROGRESS),
        subject: i18n.t('ticket:subject.assigned'),
        detail: i18n.t('ticket:detail.assigned', {
          adminFullName: setValue(updatedByFullName),
          technicianFullName: isEmpty(users)
            ? '-'
            : setValue(users[0].fullName),
          assignStartDate: setFormatAssignDate(assignStart),
          assignStartTime: setFormatTime(assignStart),
          assignEndTime: setFormatTime(assignEnd),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_CHECKED_IN:
      content = {
        status: renderStatus(TASK_PROGRESS.IN_PROGRESS),
        subject: i18n.t('ticket:subject.checkIn'),
        detail: i18n.t('ticket:detail.checkIn', {
          technicianFullName: getName(updatedByFullName, checkInByUser),
          checkedInDate: setFormatAssignDate(checkInAt),
          checkedInTime: setFormatTime(checkInAt),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_PENDING:
      content = {
        status: renderStatus(TASK_PROGRESS.PENDING),
        subject: i18n.t('ticket:subject.pending'),
        detail: i18n.t('ticket:detail.pending', {
          technicianFullName: getName(updatedByFullName, pendingByUser),
          pendingCount,
          pendingDate: setFormatAssignDate(pendingAt),
          pendingTime: setFormatTime(pendingAt),
          pendingReason: setValue(pendingReasonValue),
          pendingDetail: setValue(pendingDetail),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_COMPLETED_BY_TECHNICIAN:
      content = {
        status: renderStatus(TASK_PROGRESS.DONE),
        subject: i18n.t('ticket:subject.doneTechnician'),
        detail: i18n.t('ticket:detail.doneTechnician', {
          technicianFullName: getName(updatedByFullName, completedByUser),
          taskType: setValue(taskTypeValue),
          taskCode: setValue(code),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_COMPLETED_BY_ADMIN:
      content = {
        status: renderStatus(TASK_PROGRESS.DONE),
        subject: i18n.t('ticket:subject.doneCompletedClosedIssue'),
        detail: i18n.t('ticket:detail.doneCompletedClosedIssue', {
          adminFullName: getName(updatedByFullName, completedByUser),
          completedClosedIssue: setValue(completedTaskIssueValue),
          taskClosedDetail: setValue(completedDetail),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_REJECTED:
      content = {
        status: renderStatus(TASK_PROGRESS.REJECTED),
        subject: i18n.t('ticket:subject.rejected'),
        detail: i18n.t('ticket:detail.rejected', {
          reason: setValue(rejectedReasonValue),
          detail: setValue(rejectedDetail),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_CLOSED:
      content = {
        status: renderStatus(TASK_PROGRESS.CLOSED),
        subject: i18n.t('ticket:subject.closed'),
        detail: i18n.t('ticket:detail.closed', {
          reason: setValue(closedReasonValue),
          detail: setValue(closedTaskDetail),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_ASSIGN_UPLOAD_BEFORE_PHOTOS:
      const {
        technicianFullName: technicianFullNameAssignBefore,
        uploadPhotoDate: uploadAssignBeforePhotoDate,
        uploadPhotoTime: uploadAssignBeforePhotoTime,
        photoList: listUploadAssignBefore,
      } = getPhotoContent(assignBeforePhotos)

      const detailText = i18n.t('ticket:detail.assignUploadBeforePhotos', {
        technicianFullName: technicianFullNameAssignBefore,
        imageCount: assignBeforePhotos?.length || 0,
        uploadPhotoDate: setValue(uploadAssignBeforePhotoDate),
        uploadPhotoTime: setValue(uploadAssignBeforePhotoTime),
        interpolation: { escapeValue: false },
      })

      content = {
        status: renderStatus(TASK_PROGRESS.IN_PROGRESS),
        subject: i18n.t('ticket:subject.assignUploadBeforePhotos'),
        detail: !isEmpty(listUploadAssignBefore)
          ? renderPreviewImages(listUploadAssignBefore, detailText)
          : detailText,
      }
      break

    case TASK_EVENT_TYPE.TASK_ASSIGN_UPLOAD_AFTER_PHOTOS:
      const {
        technicianFullName: technicianFullNameAssignAfter,
        uploadPhotoDate: uploadAssignAfterPhotoDate,
        uploadPhotoTime: uploadAssignAfterPhotoTime,
        photoList: listUploadAfter,
      } = getPhotoContent(assignAfterPhotos)

      const detailAfterText = i18n.t('ticket:detail.assignUploadAfterPhotos', {
        technicianFullName: technicianFullNameAssignAfter,
        imageCount: assignAfterPhotos?.length || 0,
        uploadPhotoDate: setValue(uploadAssignAfterPhotoDate),
        uploadPhotoTime: setValue(uploadAssignAfterPhotoTime),
        interpolation: { escapeValue: false },
      })

      content = {
        status: renderStatus(TASK_PROGRESS.DONE),
        subject: i18n.t('ticket:subject.assignUploadAfterPhotos'),
        detail: !isEmpty(listUploadAfter)
          ? renderPreviewImages(listUploadAfter, detailAfterText)
          : detailAfterText,
      }
      break

    case TASK_EVENT_TYPE.TASK_TRANSFERRING_REQUEST:
      content = {
        status: renderStatus(TASK_PROGRESS.TRANSFERRING),
        subject: i18n.t('ticket:subject.transferring'),
        detail: i18n.t('ticket:detail.transferring', {
          technicianFullName: setValue(updatedByFullName),
          transferringDate: setFormatAssignDate(transferringAt),
          transferringTime: setFormatTime(transferringAt),
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_PRODUCT_COMPLETED:
      content = {
        status: renderStatus(TASK_PROGRESS.IN_PROGRESS),
        subject: i18n.t('ticket:subject.doneTechnician'),
        detail: i18n.t('ticket:detail.productCompleted', {
          technicianFullName: setValue(updatedByFullName),
          taskType: setValue(taskTypeValue),
          productName,
          interpolation: { escapeValue: false },
        }),
      }
      break

    case TASK_EVENT_TYPE.TASK_PRODUCT_UPLOAD_BEFORE_PHOTOS:
      const beforeProductPhotos: IUploadPhoto[] | null = getProductPhotos(
        typeEvent,
        product,
      )

      const { photoList: listUploadProductBefore } =
        getPhotoContent(beforeProductPhotos)

      const detailProductBeforeText = i18n.t(
        'ticket:detail.productUploadBeforePhotos',
        {
          productName,
          interpolation: { escapeValue: false },
        },
      )

      content = {
        status: renderStatus(TASK_PROGRESS.IN_PROGRESS),
        subject: i18n.t('ticket:subject.uploadPhotos'),
        detail: !isEmpty(listUploadProductBefore)
          ? renderPreviewImages(
              listUploadProductBefore,
              detailProductBeforeText,
            )
          : detailProductBeforeText,
      }
      break

    case TASK_EVENT_TYPE.TASK_PRODUCT_UPLOAD_AFTER_PHOTOS:
      const afterProductPhotos: IUploadPhoto[] | null = getProductPhotos(
        typeEvent,
        product,
      )

      const {
        technicianFullName: technicianFullNameProductAfter,
        uploadPhotoDate: uploadProductAfterPhotoDate,
        uploadPhotoTime: uploadProductAfterPhotoTime,
        photoList: listUploadProductAfter,
      } = getPhotoContent(afterProductPhotos)

      const detailProductAfterText = i18n.t(
        'ticket:detail.productUploadAfterPhotos',
        {
          technicianFullName: technicianFullNameProductAfter,
          productName,
          imageCount: afterProductPhotos?.length || 0,
          uploadPhotoDate: setValue(uploadProductAfterPhotoDate),
          uploadPhotoTime: setValue(uploadProductAfterPhotoTime),
          interpolation: { escapeValue: false },
        },
      )

      content = {
        status: renderStatus(TASK_PROGRESS.IN_PROGRESS),
        subject: i18n.t('ticket:subject.assignUploadAfterPhotos'),
        detail: !isEmpty(listUploadProductAfter)
          ? renderPreviewImages(listUploadProductAfter, detailProductAfterText)
          : detailProductAfterText,
      }
      break
  }

  if (content.detail) {
    content = {
      ...content,
      detail: <div className='tbody__cell-display-all'>{content.detail}</div>,
    }
  }

  return content
}
